import { createRef, useState } from 'react'
import { SpcOverrides } from '../../../shared/types/spc.ts'
import { Input } from '../../components/input.tsx'
import { ModalSubmitDialog } from '../../components/modalSubmitDialog.tsx'
import { ToolTipIcon } from '../../components/toolTipIcon.tsx'


interface Props {
    overrides: SpcOverrides | null
    setOverrides: (overrides: SpcOverrides | null) => void
    onClose: () => void
}

export function SpcOverridesDialog({
    overrides,
    setOverrides,
    onClose,
}: Props) {

    const formRef = createRef<HTMLFormElement>();
    const [mean, setMean] = useState(overrides?.mean ? overrides.mean.toString() : '');
    const [stddev, setStddev] = useState(overrides?.stddev ? overrides.stddev.toString() : '');
    const [minValue, setMinValue] = useState(overrides?.minValue ? overrides.minValue.toString() : '');
    const [maxValue, setMaxValue] = useState(overrides?.maxValue ? overrides.maxValue.toString() : '');


    function handleSubmit() {
        const meanParsed = mean ? parseFloat(mean) : null;
        const stddevParsed = stddev ? parseFloat(stddev) : null;
        const minValueParsed = minValue ? parseFloat(minValue) : null;
        const maxValueParsed = maxValue ? parseFloat(maxValue) : null;

        if (meanParsed != null && isNaN(meanParsed)) {
            alert('Invalid mean.');
            return;
        }
        if (stddevParsed != null && (isNaN(stddevParsed) || stddevParsed <= 0)) {
            alert('Invalid stddev. Must be > 0.');
            return;
        }
        if (minValueParsed != null && (isNaN(minValueParsed) || minValueParsed <= 0)) {
            alert('Invalid min value. Must be > 0.');
            return;
        }
        if (maxValueParsed != null && (isNaN(maxValueParsed) || maxValueParsed <= 0)) {
            alert('Invalid max value. Must be > 0.');
            return;
        }

        if (meanParsed == null && stddevParsed == null && minValueParsed == null && maxValueParsed == null) {
            setOverrides(null);
        } else {
            setOverrides({
                ...(meanParsed != null && { mean: meanParsed }),
                ...(stddevParsed != null && { stddev: stddevParsed }),
                ...(minValueParsed != null && { minValue: minValueParsed }),
                ...(maxValueParsed != null && { maxValue: maxValueParsed }),
            });
        }
        onClose();
    }

    return (
        <ModalSubmitDialog
            isOpen={true}
            onSubmit={handleSubmit}
            onCancel={onClose}
        >
            <form
                ref={formRef}
                onKeyDown={e => e.key === 'Enter' && formRef.current?.requestSubmit()}
            >
                <div className='dialog-form__field--with-margins' >
                    <div>Mean</div>
                    <Input
                        className='dialog-form__input'
                        type='number'
                        value={mean}
                        min={'0'}
                        onChange={e => setMean(e.target.value)}
                    />
                </div>

                <div className='dialog-form__field--with-margins' >
                    <div>Stddev</div>
                    <Input
                        className='dialog-form__input'
                        type='number'
                        value={stddev}
                        min={'0.001'}
                        onChange={e => setStddev(e.target.value)}
                    />
                </div>

                <div className='dialog-form__field--with-margins' >
                    <div>
                        Min/max value
                        <ToolTipIcon>
                            Limits min/max value when calculating mean and stddev.<br />
                            E.g. if min value is set to 5, all values below 5 will be treated as 5.<br />
                            Useful to limit the effect of outliers.
                        </ToolTipIcon>
                    </div>
                    <Input
                        className='dialog-form__input'
                        type='number'
                        value={minValue}
                        min={'0.001'}
                        onChange={e => setMinValue(e.target.value)}
                    />
                    <Input
                        className='dialog-form__input'
                        type='number'
                        value={maxValue}
                        min={'0.001'}
                        onChange={e => setMaxValue(e.target.value)}
                    />
                </div>
            </form>
        </ModalSubmitDialog>
    )
}
